import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { ConfirmationModal } from "./ConfirmationModal";
import {
  CvEditorSession,
  CvEditorSessionStatus,
} from "../types/cv-editor.types";
import {
  archiveSession,
  fetchSessionHistory,
} from "../../../../../../../services/cv-editor.service";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";

const INITIAL_VISIBLE_COUNT = 5;
const EXPAND_BY_COUNT = 5;

// Helper to get styling and text for each status
const getStatusInfo = (
  status: CvEditorSessionStatus,
  t: (key: string) => string
) => {
  switch (status) {
    case CvEditorSessionStatus.COMPLETED:
      return {
        text: t("history.status.completed"),
        className: "border-green-500 bg-green-50",
        textColor: "text-green-800",
      };
    case CvEditorSessionStatus.INTERVIEW_POSITIONS_IN_PROGRESS:
    case CvEditorSessionStatus.INTERVIEW_ABOUT_ME_IN_PROGRESS:
    case CvEditorSessionStatus.FINAL_GENERATION:
      return {
        text: t("history.status.pending"),
        className: "border-yellow-400 bg-yellow-50",
        textColor: "text-yellow-800",
      };
    case CvEditorSessionStatus.ARCHIVED:
      return {
        text: t("history.status.archived"),
        className: "border-gray-400 bg-gray-100 opacity-70",
        textColor: "text-gray-600",
      };
    case CvEditorSessionStatus.FAILED:
      return {
        text: t("history.status.failed"),
        className: "border-red-500 bg-red-50",
        textColor: "text-red-800",
      };
    default:
      return {
        text: t("history.status.pending"),
        className: "border-yellow-400 bg-yellow-50",
        textColor: "text-yellow-800",
      };
  }
};

export const SessionHistory: React.FC = () => {
  const { t } = useTranslation("cvEditor");
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToArchive, setSessionToArchive] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const {
    data: sessions = [],
    isLoading,
    error,
  } = useQuery("cvSessionHistory", fetchSessionHistory);

  const archiveMutation = useMutation(
    (sessionId: number) => archiveSession(sessionId),
    {
      onSuccess: () => {
        // Refetch both history and the current session state
        queryClient.invalidateQueries("cvSessionHistory");
        queryClient.invalidateQueries("currentCvSession");
        setDeleteModalOpen(false);
        setSessionToArchive(null);
      },
      onError: (err) => console.error("Failed to archive session:", err),
    }
  );

  const sessionsToShow = sessions.slice(0, visibleCount);
  const hasMoreSessions = sessions.length > visibleCount;
  const isExpanded = visibleCount > INITIAL_VISIBLE_COUNT;
  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + EXPAND_BY_COUNT);
  };

  const handleCollapse = () => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const handleDownload = (session: CvEditorSession) => {
    if (!session?.finalCvJson) return;
    const filename = `cv-data-${
      new Date(session.createdAt).toISOString().split("T")[0]
    }.json`;
    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(session.finalCvJson, null, 2)
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = filename;
    link.click();
  };

  const handleArchiveClick = (sessionId: number) => {
    setSessionToArchive(sessionId);
    setDeleteModalOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (sessionToArchive !== null) {
      archiveMutation.mutate(sessionToArchive);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading)
    return (
      <div className="text-center p-8 text-gray-500">
        {t("history.loading")}
      </div>
    );
  if (error)
    return (
      <div className="text-center p-8 text-red-800">{t("history.error")}</div>
    );
  if (!sessions || sessions.length === 0)
    return (
      <div className="text-center p-8 text-gray-500">
        {t("history.noSessions")}
      </div>
    );

  return (
    <>
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 ">
            {t("history.title")}
          </h2>
          {isExpanded && (
            <Button
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={handleCollapse}
              className="text-sm"
            >
              {t("history.collapse")}
            </Button>
          )}{" "}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <ul className="divide-y divide-gray-200">
            {sessionsToShow.map((session: any) => {
              const statusInfo = getStatusInfo(session.status, t);
              const isDownloadable =
                session.status === CvEditorSessionStatus.COMPLETED &&
                !!session.finalCvJson;

              return (
                <li
                  key={session.id}
                  className={`p-4 flex flex-row items-center justify-between border-l-4 ${statusInfo.className}`}
                >
                  <div className="flex-1 mb-4 sm:mb-0">
                    <p className="text-sm font-medium text-gray-900">
                      {t("history.sessionOn")}{" "}
                      {formatDateTime(session.createdAt)}
                    </p>
                    <p
                      className={`text-xs font-semibold uppercase tracking-wider mt-1 ${statusInfo.textColor}`}
                    >
                      {statusInfo.text}
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={() => handleDownload(session)}
                      disabled={!isDownloadable}
                      className="p-2 text-gray-500 hover:text-black disabled:text-gray-300 disabled:cursor-not-allowed"
                      title={
                        t("history.downloadAction") ||
                        "missing static add to file"
                      }
                    >
                      <DownloadIcon />
                    </button>
                    <button
                      onClick={() => handleArchiveClick(session.id)}
                      disabled={
                        archiveMutation.isLoading &&
                        archiveMutation.variables === session.id
                      }
                      className="p-2 text-gray-500 hover:text-red-800 disabled:opacity-50"
                      title={
                        t("history.deleteAction") ||
                        "missing static add to file"
                      }
                    >
                      <DeleteForeverIcon />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        {hasMoreSessions && (
          <div className="mt-4 text-center">
            <Button
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={handleShowMore}
              className="flex items-start text-sm"
            >
              {t("history.showMore")}
            </Button>
          </div>
        )}
      </div>
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleArchiveConfirm}
        title={t("history.deleteModalTitle")}
        message={t("history.message")}
        confirmText={t("history.deleteModalConfirm")}
        cancelText={t("history.deleteModalCancel")}
        confirmVariant="destructive"
        isLoading={archiveMutation.isLoading}
      />
    </>
  );
};
